import { ContributionCoverage } from '../components/ContributionCoverage';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { officialFacilityFacts, seatLayouts, starterVenues } from '@anjabom/core';
import { Breadcrumb } from '../components/Breadcrumb';
import { SurfaceBanner } from '../components/SurfaceBanner';
import { readSurfaceState } from '../lib/surface-state';

type CoverageLedger = Readonly<{
  weekAgo: { takenAt: string; venues: number; layoutVersions: number; exactSeats: number; reviews12m: number } | null;
  weeklyChange: { venues: number; layoutVersions: number; exactSeats: number; reviews12m: number } | null;
}>;

type Coverage = Readonly<{
  catalog?: { venues: number; layoutVersions: number; spaces: number };
  venues: number;
  layoutVersions: number;
  spaces: number;
  exactSeats: number | null;
  reviews12m: number | null;
  takenAt: string | null;
  status: string;
  ledger: CoverageLedger;
}>;

const fallbackCoverage: Coverage = {
  venues: starterVenues.length,
  layoutVersions: seatLayouts.length,
  spaces: starterVenues.reduce((count, venue) => count + venue.spaces.length, 0),
  exactSeats: null,
  reviews12m: null,
  takenAt: null,
  status: 'snapshot_pending',
  ledger: { weekAgo: null, weeklyChange: null },
};

export function CoveragePage() {
  const [coverage, setCoverage] = useState<Coverage>(fallbackCoverage);
  useEffect(() => {
    void fetch('/api/coverage').then(async (response) => response.ok ? response.json() : null).then((value) => {
      if (!value || typeof value !== 'object') return;
      const source = value as Partial<Coverage>;
      setCoverage({
        catalog: source.catalog ?? { venues: fallbackCoverage.venues, layoutVersions: fallbackCoverage.layoutVersions, spaces: fallbackCoverage.spaces },
        venues: Number(source.venues ?? fallbackCoverage.venues),
        layoutVersions: Number(source.layoutVersions ?? fallbackCoverage.layoutVersions),
        spaces: Number(source.spaces ?? fallbackCoverage.spaces),
        exactSeats: typeof source.exactSeats === 'number' ? source.exactSeats : null,
        reviews12m: typeof source.reviews12m === 'number' ? source.reviews12m : null,
        takenAt: typeof source.takenAt === 'string' ? source.takenAt : null,
        status: typeof source.status === 'string' ? source.status : 'snapshot_unavailable',
        ledger: source.ledger && typeof source.ledger === 'object' ? source.ledger as CoverageLedger : { weekAgo: null, weeklyChange: null },
      });
    }).catch(() => undefined);
  }, []);
  const catalog = coverage.catalog ?? fallbackCoverage;
  const updateLabel = coverage.takenAt ? `마지막 확인 ${new Date(coverage.takenAt).toLocaleDateString('ko-KR')}` : coverage.status === 'snapshot_pending' ? '첫 현황을 준비하고 있어요.' : '좌석 현황을 불러오지 못했어요.';
  return (
    <section className="page narrow stack-lg">
      <Breadcrumb items={[{ label: '홈', href: '/' }, { label: '확인 범위' }]} />
      <SurfaceBanner state={readSurfaceState()} empty="아직 0곳이에요." />
      <div><p className="eyebrow">현재 확인 범위</p><h1>앉아봄이 지금 아는 것</h1><p className="lede">장소 {catalog.venues}곳 · 홀·상영관 {catalog.spaces}개 · 시설 배치 {catalog.layoutVersions}개 · 인증된 좌석 {coverage.exactSeats === null ? '집계 대기' : `${coverage.exactSeats}석`}</p></div>
      <div className="notice"><span className="notice-mark">i</span><span>아직 모르는 게 훨씬 많아요. 기록이 없는 좌석은 모름으로 보여드려요.</span></div>
      <section className="card stack" aria-labelledby="weekly-ledger-title">
        <div><p className="eyebrow">이번 주 변화</p><h2 id="weekly-ledger-title">이번 주에 새로 확인된 정보</h2></div>
        {coverage.ledger.weeklyChange?.exactSeats === 0 && coverage.ledger.weeklyChange.reviews12m === 0
          ? <p className="muted">지난 기준점 이후 인증 좌석과 공개 리뷰 수에 변화가 없어요.</p>
          : coverage.ledger.weeklyChange ? <>
          <p><strong>인증 좌석 {coverage.ledger.weeklyChange.exactSeats >= 0 ? '+' : ''}{coverage.ledger.weeklyChange.exactSeats}석</strong> · 공개 리뷰 {coverage.ledger.weeklyChange.reviews12m >= 0 ? '+' : ''}{coverage.ledger.weeklyChange.reviews12m}건</p>
          <p className="micro">{coverage.ledger.weekAgo ? `${new Date(coverage.ledger.weekAgo.takenAt).toLocaleDateString('ko-KR')} 현황과 비교했어요. 미인증 리뷰는 인증 좌석 수에 포함하지 않아요.` : null}</p>
        </> : <p className="muted">7일 전 기준점이 쌓이면 인증 좌석과 공개 리뷰의 변화를 함께 보여드려요. 지금은 숫자를 추정하지 않아요.</p>}
      </section>
      <div className="stack">
        {starterVenues.map((venue) => {
          const layouts = seatLayouts.filter((layout) => layout.venueId === venue.id);
          const exact = layouts.filter((layout) => layout.precision === 'seat').length;
          const section = layouts.filter((layout) => layout.precision === 'section').length;
          const booking = layouts.filter((layout) => layout.precision === 'booking').length;
          return <article className="card" key={venue.id}><h2>{venue.name}</h2><p className="muted">{venue.region} · {venue.spaces.map((space) => space.name).join(' · ')}</p><p className="micro">시설명 확인 {venue.verifiedAt} · 좌표 선택 가능한 시설 배치 {exact}종 · 구역 참고 배치 {section}종 · 예매 회차에서 확인해야 할 경로 {booking}개</p><p className="micro">배치 종류 수는 관람 후기나 실제 회차 확인 수가 아니에요.</p>{officialFacilityFacts.filter((fact) => fact.venueId === venue.id).map((fact) => <p key={fact.spaceId}>{fact.statement} <span className="micro">시설 수량 확인 {fact.checkedAt} · 회차 배치는 모름이에요.</span></p>)}<Link to={`/venue/${venue.slug}`}>장소 보기 →</Link></article>;
        })}
      </div>
      <p className="micro">장소와 시설 배치는 위 목록 기준이에요. {updateLabel} · 인증 좌석은 구매·관람·발급처 확인을 거친 공개 기록만 집계해요.</p>
    <ContributionCoverage/></section>
  );
}
